import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Controller, useFieldArray, UseFormReturn, UseFormSetValue } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, AlertCircle } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useGetIncomeCategoryQuery } from '@/services/incomeCategoryApi'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useGetBudgetDataMutation, getUniqueBudkeys, getAHKeyOptionsForBudkey, getAHTitleForAHKey, } from '@/services/dhAccountingApi'
import { BudgetDataItem } from '@/types/budget'
import Combobox from '@/components/ui/common/combobox'
import { useDebounce } from '@/hooks/use-debounce'
import { toast } from '@/components/ui/use-toast'

// Types
export interface CategoryMapping {
  category_name: string
  Budkey: string
  AHKey: string
  AHTitle: string
}

export interface IncomeCategoryMappingRow {
  income_category_id: string
  account_budget: string // This will be AHKey
  account_title: string // This will be AHTitle
  budkey: string // This will be Budkey
}

export interface RevenueSection {
  Comp: string
  BAHKey: string
  party: number | undefined
  PartyCode: string
  SysTag: number | undefined
  pkey1: string
  IAHKey: string
  sAHKey: string
  fAHKey: string
  categories: IncomeCategoryMappingRow[]
}

export interface PayloadRevenueSection {
  Comp: string
  BAHKey: string
  party: string
  PartyCode: string
  SysTag: string
  pkey1: string
  IAHKey: string
  sAHKey: string
  fAHKey: string
  categories: Record<string, CategoryMapping>
}

export interface DHAccountingPayload {
  dh_accounting: {
    income: PayloadRevenueSection
    donation: PayloadRevenueSection
    api_url: string
    organization_id: string
  }
}

// Form data structure that matches the payload
export interface DHAccountingFormData {
  dh_accounting: {
    income: RevenueSection
    donation: RevenueSection
    api_url: string
    organization_id: string
    year: string,
    bh_type: string
  }
}

interface ConfigurationFormProps {
  form: UseFormReturn<DHAccountingFormData>
  revenueType: 'donation' | 'income'
  isEditMode: boolean
  currentFormData: DHAccountingFormData
  onRevenueTypeChange: (type: 'donation' | 'income') => void
  setValue: UseFormSetValue<DHAccountingFormData>
  watchedOrgId: string
  onBudgetDataError?: (hasError: boolean) => void
}

/**
 * Transforms form data to payload format (categories array to object)
 * Note: The key in the categories object is the income_category_id (numeric id as string).
 * category_name should be the income category's name/label, not the account_title.
 */
export function transformFormDataToPayload(
  data: DHAccountingFormData,
  incomeCategories?: Array<{ id?: number; name?: string }>
) {
  // Build id-to-name map for looking up category names
  const idToNameMap = new Map<string, string>()
  if (Array.isArray(incomeCategories)) {
    incomeCategories.forEach((ic) => {
      if (ic.id && ic.name) {
        idToNameMap.set(String(ic.id), ic.name)
      }
    })
  }

  // Transform categories array to object format for both revenue types
  // Use income_category_id directly as the key
  const transformCategories = (categories: IncomeCategoryMappingRow[], revenueType: 'donation' | 'income'): Record<string, CategoryMapping> => {
    return Object.fromEntries(
      categories
        .filter(cat => cat.income_category_id && cat.income_category_id.trim() !== '')
        .map(cat => {
          // Get the income category name from the map, fallback to empty string
          const categoryName = idToNameMap.get(cat.income_category_id) || ''
          return [
            cat.income_category_id, // Use income_category_id directly as the key
            {
              category_name: categoryName, // Use income category name, not account_title
              Budkey: cat.budkey || '',
              AHKey: cat.account_budget || '',
              AHTitle: cat.account_title || ''
            }
          ]
        })
    )
  }

  return {
    dh_accounting: {
      api_url: data.dh_accounting.api_url,
      organization_id: data.dh_accounting.organization_id,
      year: data.dh_accounting.year,
      bh_type: data.dh_accounting.bh_type,
      income: JSON.stringify({
        Comp: data.dh_accounting.income.Comp,
        BAHKey: data.dh_accounting.income.BAHKey,
        party: data.dh_accounting.income.party ?? 0,
        PartyCode: data.dh_accounting.income.PartyCode,
        SysTag: data.dh_accounting.income.SysTag ?? 0,
        pkey1: data.dh_accounting.income.pkey1 || '',
        IAHKey: data.dh_accounting.income.IAHKey || '',
        sAHKey: data.dh_accounting.income.sAHKey || '',
        fAHKey: data.dh_accounting.income.fAHKey || '',
        categories: transformCategories(data.dh_accounting.income.categories, 'income')
      }),
      donation: JSON.stringify({
        Comp: data.dh_accounting.donation.Comp,
        BAHKey: data.dh_accounting.donation.BAHKey,
        party: data.dh_accounting.donation.party ?? 0,
        PartyCode: data.dh_accounting.donation.PartyCode,
        SysTag: data.dh_accounting.donation.SysTag ?? 0,
        pkey1: data.dh_accounting.donation.pkey1 || '',
        IAHKey: data.dh_accounting.donation.IAHKey || '',
        sAHKey: data.dh_accounting.donation.sAHKey || '',
        fAHKey: data.dh_accounting.donation.fAHKey || '',
        categories: transformCategories(data.dh_accounting.donation.categories, 'donation')
      }),
    }
  }
}


export const ConfigurationForm = ({
  form,
  revenueType,
  isEditMode,
  currentFormData,
  onRevenueTypeChange,
  setValue,
  watchedOrgId,
  onBudgetDataError
}: ConfigurationFormProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const {
    control,
    watch,
    formState: { errors }
  } = form

  const watchedApiUrl = watch('dh_accounting.api_url')
  const watchedYear = watch('dh_accounting.year')
  const watchedBhType = watch('dh_accounting.bh_type')

  // Debounce the watched values to prevent excessive API calls
  const debouncedOrgId = useDebounce(watchedOrgId, 500)
  const debouncedApiUrl = useDebounce(watchedApiUrl, 500)
  const debouncedYear = useDebounce(watchedYear, 500)
  const debouncedBhType = useDebounce(watchedBhType, 500)

  // Budget data state and mutation
  const [budgetData, setBudgetData] = React.useState<BudgetDataItem[] | null>(null)
  const [isBudgetDataLoading, setIsBudgetDataLoading] = React.useState(false)
  const [hasBudgetDataError, setHasBudgetDataError] = React.useState(false)
  const [getBudgetData] = useGetBudgetDataMutation()

  // Fetch budget data when debounced values change
  React.useEffect(() => {
    const fetchBudgetData = async () => {
      // Skip if required values are missing
      if (!debouncedOrgId || debouncedOrgId.trim() === '' || !debouncedApiUrl || debouncedApiUrl.trim() === '' || !debouncedYear || debouncedYear.trim() === '' || !debouncedBhType || debouncedBhType.trim() === '') {
        setBudgetData(null)
        setHasBudgetDataError(false)
        onBudgetDataError?.(false)
        return
      }

      setIsBudgetDataLoading(true)
      setHasBudgetDataError(false)
      onBudgetDataError?.(false)
      try {
        const result = await getBudgetData({
          accounting_url: debouncedApiUrl,
          company_code: parseInt(debouncedOrgId) || 0,
          current_year: parseInt(debouncedYear) || new Date().getFullYear(),
          bh_type: debouncedBhType // based on client's budget key api for selection
        }).unwrap()
        setBudgetData(result || [])
        setHasBudgetDataError(false)
        onBudgetDataError?.(false)
      } catch (error) {
        console.error('Failed to fetch budget data:', error)
        setBudgetData(null)
        setHasBudgetDataError(true)
        onBudgetDataError?.(true)
        toast({
          variant: 'destructive',
          description: 'Failed to fetch budget data. Please check your API URL and Organization ID, then try again.'
        })
      } finally {
        setIsBudgetDataLoading(false)
      }
    }

    fetchBudgetData()
  }, [debouncedOrgId, debouncedApiUrl, debouncedYear, debouncedBhType, getBudgetData, onBudgetDataError])

  const currentRevenueSection = revenueType === 'donation'
    ? currentFormData?.dh_accounting?.donation
    : currentFormData?.dh_accounting?.income

  // Validation rules for form fields
  const validationRules = {
    org_id: {
      required: 'Organization ID is required',
      maxLength: { value: 5, message: 'Org ID must not exceed 5 characters' }
    },
    accounting_url: {
      validate: (value: string) => {
        if (!value || value.trim() === '') return true
        try {
          new URL(value)
          return true
        } catch {
          return 'Please enter a valid URL'
        }
      }
    },
    AHTitle: {
      maxLength: { value: 150, message: 'Account Holder Title must not exceed 150 characters' }
    },
    BAHKey: {
      maxLength: { value: 5, message: 'Budget Holder Key must not exceed 5 characters' }
    },
    SysTag:{
      maxLength: { value: 20, message: 'System Tag must not exceed 20 characters' }
    },
    numericFields: {
      validate: (value: string) => {
        if (!value || value.trim() === '') return true
        const numValue = Number(value)
        if (isNaN(numValue)) return 'Must be a valid number'
        if (!Number.isInteger(numValue)) return 'Must be an integer'
        return true
      }
    },
    numericNumberFields: {
      validate: (value: number | undefined) => {
        if (value === undefined || value === null) return true
        if (typeof value !== 'number') return 'Must be a valid number'
        if (!Number.isInteger(value)) return 'Must be an integer'
        return true
      }
    }
  }

  // Data for dropdowns
  const { data: incomeCategoryData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: '' },
    { skip: !debouncedOrgId || debouncedOrgId.trim() === '' }
  )


  const incomeCategoryOptions = React.useMemo(() => {
    if (!Array.isArray(incomeCategoryData?.data)) return []

    // Create a map to deduplicate by id to ensure unique options
    const uniqueOptionsMap = new Map<string, { value: string; label: string }>()

    incomeCategoryData.data
      .filter((ic) => ic.id) // Only include categories with id
      .forEach((ic) => {
        const key = String(ic.id)
        // Only add if we haven't seen this id before
        if (!uniqueOptionsMap.has(key)) {
          uniqueOptionsMap.set(key, { value: key, label: ic.name })
        }
      })

    return Array.from(uniqueOptionsMap.values())
  }, [incomeCategoryData])

  // SOAP API options
  const budkeyOptions = React.useMemo(() => {
    if (!budgetData) return []
    return getUniqueBudkeys(budgetData)
  }, [budgetData])

  // const accountHeadOptions = React.useMemo(() => {
  //   if (!budgetData) return []
  //   // Create a map to deduplicate by AHKey
  //   const uniqueOptionsMap = new Map<string, { value: string; label: string; budkey: number; ahTitle: string }>()

  //   budgetData.forEach((item: BudgetDataItem) => {
  //     const key = item.AHKey.toString()
  //     // Only add if we haven't seen this AHKey before
  //     if (!uniqueOptionsMap.has(key)) {
  //       uniqueOptionsMap.set(key, {
  //         value: key,
  //         label: `${item.AHKey} - ${item.AHTitle}`,
  //         budkey: item.budKey,
  //         ahTitle: item.AHTitle
  //       })
  //     }
  //   })

  //   return Array.from(uniqueOptionsMap.values())
  // }, [budgetData])

  // Helper function to get account head options filtered by budkey
  // use the exported helper from dhAccountingApi so that the shape of
  // each option matches what <Combobox> expects ({ value, label, ahTitle }).
  const getAccountHeadOptionsForBudkey = React.useCallback((budkey: string) => {
    if (!budkey || !budgetData) return []
    const budkeyNum = parseInt(budkey)
    // this returns list of { value, label, ahTitle }
    return getAHKeyOptionsForBudkey(budgetData, budkeyNum)
  }, [budgetData])

  // accountTitleMapping is no longer required because the combobox options
  // include the ahTitle and we copy it into the form value when a selection
  // is made. Keep the helper around for any future lookups, but we don't
  // currently consume this memo anywhere else.
 
  // Field Arrays - separate for donation and income to preserve data when switching
  const donationCategories = useFieldArray({
    control,
    name: 'dh_accounting.donation.categories'
  })

  const incomeCategories = useFieldArray({
    control,
    name: 'dh_accounting.income.categories'
  })

  // Use the appropriate field array based on revenue type
  const currentCategories = revenueType === 'donation' ? donationCategories : incomeCategories

  return (
    <div className="space-y-4">
      {/* Connection Settings */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
          Connection Settings
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* API Endpoint URL - Takes 2 columns */}
          <div className="space-y-2 sm:col-span-2 lg:col-span-2">
            <Label className="text-sm font-medium text-gray-700">API Endpoint URL</Label>
            {isEditMode ? (
              <Controller
                name="dh_accounting.api_url"
                control={control}
                rules={validationRules.accounting_url}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="https://api.example.com/accounting"
                    value={field.value || ''}
                    className="w-full"
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm min-h-[2.5rem] flex items-center">
                <span className="text-gray-900 break-all" title={currentFormData?.dh_accounting?.api_url || 'Not configured'}>
                  {currentFormData?.dh_accounting?.api_url || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.api_url && (
              <p className="text-red-500 text-xs">{errors.dh_accounting.api_url.message}</p>
            )}
          </div>

          {/* Org ID - Takes 1 column */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Organization ID <span className="text-red-500">*</span></Label>
            {isEditMode ? (
              <Controller
                name="dh_accounting.organization_id"
                control={control}
                rules={validationRules.org_id}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="e.g., 12345"
                    value={field.value || ''}
                    maxLength={5}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm min-h-[2.5rem] flex items-center">
                <span className="text-gray-900" title={currentFormData?.dh_accounting?.organization_id || 'Not configured'}>
                  {currentFormData?.dh_accounting?.organization_id || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.organization_id && (
              <p className="text-red-500 text-xs">{errors.dh_accounting.organization_id.message}</p>
            )}
          </div>
        </div>

        {/* Year and BH Type Fields - Same row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Year <span className="text-red-500">*</span></Label>
            {isEditMode ? (
              <Controller
                name="dh_accounting.year"
                control={control}
                rules={{
                  required: 'Year is required',
                  pattern: {
                    value: /^\d{4}$/,
                    message: 'Year must be a 4-digit number'
                  }
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="e.g., 2025"
                    value={field.value || ''}
                    maxLength={4}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm min-h-[2.5rem] flex items-center">
                <span className="text-gray-900" title={currentFormData?.dh_accounting?.year || 'Not configured'}>
                  {currentFormData?.dh_accounting?.year || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.year && (
              <p className="text-red-500 text-xs">{errors.dh_accounting.year.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">BH Type <span className="text-red-500">*</span></Label>
            {isEditMode ? (
              <Controller
                name="dh_accounting.bh_type"
                control={control}
                rules={{
                  required: 'BH Type is required'
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="e.g., I"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm min-h-[2.5rem] flex items-center">
                <span className="text-gray-900" title={currentFormData?.dh_accounting?.bh_type || 'Not configured'}>
                  {currentFormData?.dh_accounting?.bh_type || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.bh_type && (
              <p className="text-red-500 text-xs">{errors.dh_accounting.bh_type.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Type Selection - Toggle Switch */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">Revenue Type</Label>
        <div className="flex rounded-md overflow-hidden border w-fit" key={`revenue-toggle-${revenueType}`}>
          <button
            type="button"
            onClick={() => onRevenueTypeChange('donation')}
            aria-selected={revenueType === 'donation'}
            className={
              revenueType === 'donation'
                ? "py-2 px-6 text-sm transition-colors font-medium border-r bg-primary text-white"
                : "py-2 px-6 text-sm transition-colors font-medium border-r bg-white text-gray-700 hover:bg-gray-50"
            }
          >
            Donation
          </button>
          <button
            type="button"
            onClick={() => onRevenueTypeChange('income')}
            aria-selected={revenueType === 'income'}
            className={
              revenueType === 'income'
                ? "py-2 px-6 text-sm transition-colors font-medium bg-primary text-white"
                : "py-2 px-6 text-sm transition-colors font-medium bg-white text-gray-700 hover:bg-gray-50"
            }
          >
            Income
          </button>
        </div>
      </div>

      {/* Donation/Income Configuration */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
          {revenueType === 'donation' ? 'Donation' : 'Income'} Revenue Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">Company Code</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.Comp`}
                control={control}
                rules={validationRules.numericFields}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Company Code"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.Comp || 'Not configured'}>
                  {currentRevenueSection?.Comp || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.Comp && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.Comp?.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">Bank Account Holder Key</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.BAHKey`}
                control={control}
                rules={validationRules.numericFields}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Bank Account Holder Key"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.BAHKey || 'Not configured'}>
                  {currentRevenueSection?.BAHKey || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.BAHKey && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.BAHKey?.message}</p>
            )}
          </div>


          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">Party</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.party`}
                control={control}
                rules={validationRules.numericNumberFields}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Party"
                    className="w-full text-sm"
                    value={field.value?.toString() || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === '') {
                        field.onChange(undefined)
                      } else {
                        const numValue = Number(value)
                        field.onChange(isNaN(numValue) ? undefined : numValue)
                      }
                    }}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.party !== undefined ? String(currentRevenueSection.party) : 'Not configured'}>
                  {currentRevenueSection?.party !== undefined ? String(currentRevenueSection.party) : 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.party && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.party?.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">Party Code</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.PartyCode`}
                control={control}
                rules={validationRules.numericFields}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Party Code"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.PartyCode || 'Not configured'}>
                  {currentRevenueSection?.PartyCode || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.PartyCode && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.PartyCode?.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">System Tag</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.SysTag`}
                control={control}
                rules={validationRules.SysTag}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="System Tag"
                    className="w-full text-sm"
                    value={field.value?.toString() || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value)
                    }}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.SysTag !== undefined ? String(currentRevenueSection.SysTag) : 'Not configured'}>
                  {currentRevenueSection?.SysTag !== undefined ? String(currentRevenueSection.SysTag) : 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.SysTag && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.SysTag?.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">PKey1</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.pkey1`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="PKey1"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.pkey1 || 'Not configured'}>
                  {currentRevenueSection?.pkey1 || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.pkey1 && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.pkey1?.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">IAHKey</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.IAHKey`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="IAHKey"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.IAHKey || 'Not configured'}>
                  {currentRevenueSection?.IAHKey || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.IAHKey && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.IAHKey?.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">sAHKey</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.sAHKey`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="sAHKey"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.sAHKey || 'Not configured'}>
                  {currentRevenueSection?.sAHKey || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.sAHKey && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.sAHKey?.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">fAHKey</Label>
            {isEditMode ? (
              <Controller
                name={`dh_accounting.${revenueType}.fAHKey`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="fAHKey"
                    className="w-full text-sm"
                    value={field.value || ''}
                  />
                )}
              />
            ) : (
              <div className="p-2 bg-gray-50 border rounded text-sm">
                <span className="text-gray-900 break-all" title={currentRevenueSection?.fAHKey || 'Not configured'}>
                  {currentRevenueSection?.fAHKey || 'Not configured'}
                </span>
              </div>
            )}
            {errors.dh_accounting?.[revenueType]?.fAHKey && (
              <p className="text-red-500 text-xs">{errors.dh_accounting?.[revenueType]?.fAHKey?.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Income Category Configuration */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">
          Income Category Configuration <span className="text-xs text-gray-500">(requires Org ID & API URL)</span>
        </h3>

        {isEditMode && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                // Check if the last row is complete before allowing new row
                const lastRowIndex = currentCategories.fields.length - 1
                if (lastRowIndex >= 0) {
                  const lastRow = watch(`dh_accounting.${revenueType}.categories.${lastRowIndex}`)
                  const isLastRowComplete =
                    lastRow?.income_category_id &&
                    lastRow.income_category_id.trim() !== '' &&
                    lastRow?.budkey &&
                    lastRow.budkey.trim() !== '' &&
                    lastRow?.account_budget &&
                    lastRow.account_budget.trim() !== ''

                  if (!isLastRowComplete) {
                    toast({
                      variant: 'destructive',
                      description: 'Please complete all required fields (Income Category, Budkey, and Account Head) in the current row before adding a new row.'
                    })
                    return
                  }
                }
                currentCategories.append({ income_category_id: '', account_budget: '', account_title: '', budkey: '' })
              }}
              className="flex items-center gap-1"
            >
              <Plus className="h-3 w-3" /> Add Row
            </Button>
          </div>
        )}
        <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-3">
          {currentCategories.fields.map((field, index) => (
            <div key={field.id} className="relative border rounded-md p-3 bg-gray-50/40">
              {isEditMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => currentCategories.remove(index)}
                  className="absolute top-2 right-2 h-7 w-7 p-0 text-red-600"
                  title={currentCategories.fields.length === 1 ? 'At least one row required' : 'Remove row'}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Income Category <span className="text-red-500">*</span></Label>
                  {isEditMode ? (
                    <Controller
                      name={`dh_accounting.${revenueType}.categories.${index}.income_category_id` as const}
                      control={control}
                      rules={{
                        required: 'Income Category is required'
                      }}
                      render={({ field }) => {
                        // Get all selected income category IDs from other rows (excluding current row)
                        const allSelectedIds = watch(`dh_accounting.${revenueType}.categories`)
                          .map((cat: IncomeCategoryMappingRow, idx: number) =>
                            idx !== index ? cat.income_category_id : null
                          )
                          .filter((id: string | null) => id && id.trim() !== '') as string[]

                        // Filter options to exclude already selected categories (allow current selection)
                        const availableOptions = incomeCategoryOptions.filter(
                          (opt: { value: string; label: string }) =>
                            !allSelectedIds.includes(opt.value) || opt.value === field.value
                        )

                        return (
                          <Combobox
                            list={availableOptions}
                            defaultValue={
                              availableOptions.find(
                                (option) => option.value === field.value
                              )?.label || ""
                            }
                            setFilter={(selectedLabel: string) => {
                              const selectedOption = availableOptions.find(
                                (option) => option.label === selectedLabel
                              );
                              const newValue = selectedOption ? selectedOption.value : "";
                              field.onChange(newValue);
                            }}
                            label="Income Category"
                            showLabel={false}
                            disabled={false}
                            className="w-full"
                          />
                        )
                      }}
                    />
                  ) : (
                    <div className="p-2 bg-gray-50 border rounded text-sm">
                      <span className="text-gray-900 break-all">
                        {(() => {
                          const categoryId = currentRevenueSection?.categories?.[index]?.income_category_id
                          const option = incomeCategoryOptions?.find((o: { value: string; label: string }) => o.value === categoryId)
                          return option ? option.label : 'Not configured'
                        })()}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const incomeCategoryError = errors.dh_accounting?.[revenueType]?.categories?.[index]?.income_category_id;
                    return incomeCategoryError && (
                      <p className="text-red-500 text-xs">{incomeCategoryError.message}</p>
                    );
                  })()}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Budkey <span className="text-red-500">*</span></Label>
                  {isEditMode ? (
                    <Controller
                      name={`dh_accounting.${revenueType}.categories.${index}.budkey` as const}
                      control={control}
                      rules={{
                        required: 'Budkey is required'
                      }}
                      render={({ field }) => (
                        <Combobox
                          list={budkeyOptions}
                          defaultValue={
                            budkeyOptions.find(
                              (option) => option.value === field.value
                            )?.label || ""
                          }
                          setFilter={(selectedLabel: string) => {
                            const selectedOption = budkeyOptions.find(
                              (option) => option.label === selectedLabel
                            );
                            field.onChange(
                              selectedOption
                                ? selectedOption.value
                                : ""
                            );
                          }}
                          label="Budkey"
                          showLabel={false}
                          disabled={false}
                          className="w-full"
                        />
                      )}
                    />
                  ) : (
                    <div className="p-2 bg-gray-50 border rounded text-sm">
                      <span className="text-gray-900 break-all">
                        {(() => {
                          const budkey = currentRevenueSection?.categories?.[index]?.budkey
                          const option = budkeyOptions.find((o: { value: string; label: string }) => o.value === budkey)
                          return option ? option.label : (budkey || 'Not configured')
                        })()}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const budkeyError = errors.dh_accounting?.[revenueType]?.categories?.[index]?.budkey;
                    return budkeyError && (
                      <p className="text-red-500 text-xs">{budkeyError.message}</p>
                    );
                  })()}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Account Head <span className="text-red-500">*</span></Label>
                  {isEditMode ? (
                    <Controller
                      name={`dh_accounting.${revenueType}.categories.${index}.account_budget` as const}
                      control={control}
                      rules={{
                        required: 'Account Head is required'
                      }}
                      render={({ field }) => {
                        // Get the current budkey for this row to filter options
                        const currentBudkey = watch(`dh_accounting.${revenueType}.categories.${index}.budkey`)
                        const filteredOptions = currentBudkey ? getAccountHeadOptionsForBudkey(currentBudkey) : []
                        
                        return (
                          <Combobox
                            list={filteredOptions}
                            defaultValue={
                              filteredOptions.find(
                                (option) => option.value === field.value
                              )?.label || ""
                            }
                            setFilter={(selectedLabel: string) => {
                              const selectedOption = filteredOptions.find(
                                (option) => option.label === selectedLabel
                              );
                              const newValue = selectedOption ? selectedOption.value : "";
                              field.onChange(newValue);
                              // Also set the account_title (AHTitle) based on the selected AHKey
                              if (selectedOption) {
                                setValue(`dh_accounting.${revenueType}.categories.${index}.account_title`, selectedOption.ahTitle)
                              }
                            }}
                            label="Account Head"
                            showLabel={false}
                            disabled={false}
                            className="w-full"
                          />
                        )
                      }}
                    />
                  ) : (
                    <div className="p-2 bg-gray-50 border rounded text-sm">
                      <span className="text-gray-900 break-all">
                          {(() => {
                            const ahKey = currentRevenueSection?.categories?.[index]?.account_budget
                            const budkey = currentRevenueSection?.categories?.[index]?.budkey
                            if (ahKey && budkey && budgetData) {
                              // find the label using the helper so it matches the
                              // same options that would have been shown in edit mode
                              const options = getAHKeyOptionsForBudkey(budgetData, parseInt(budkey))
                              const option = options.find((o: { value: string; label: string }) => o.value === ahKey)
                              return option ? option.label : ahKey
                            }
                            return ahKey || 'Not configured'
                          })()}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const accountBudgetError = errors.dh_accounting?.[revenueType]?.categories?.[index]?.account_budget;
                    return accountBudgetError && (
                      <p className="text-red-500 text-xs">{accountBudgetError.message}</p>
                    );
                  })()}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Account Title</Label>
                  <div className="p-2 bg-gray-50 border rounded text-sm">
                    <span className="text-gray-900 break-all">
                      {isEditMode
                        ? (watch(`dh_accounting.${revenueType}.categories.${index}.account_title`) || 'Select Account Head first')
                        : (currentRevenueSection?.categories?.[index]?.account_title || 'Not configured')
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

